/**
 * Who owns an overdue must-do, so it never goes quiet between owners.
 *
 * A must-do is an open high-priority todo (priority 1 or 2). Its owners, in
 * order:
 *
 * 1. Its reminder: the 30- and 10-minute heads-ups before it is due. The
 *    reminder row ends shortly after the due time (lifecycle.ts).
 * 2. The overdue obligation: from the due time until it is
 *    `OVERDUE_OBLIGATION_DAYS` overdue, one "overdue tasks" notice lists every
 *    such must-do (server/overdueTaskNotice.ts). It ends for a task when the
 *    task is done, deferred or given a later due date.
 * 3. The stale task digest: past the cap, the task is stale work, and the
 *    digest always counts it as stale enough to surface (staleTaskHygiene.ts).
 *
 * Other todos keep the plain path: a reminder that lasts a day after the due
 * time, then the stale digest by its own score.
 *
 * Pure and client-safe.
 */
import { isHighTaskPriority } from "@/src/features/tasks/taskModel";
import { notificationTime } from "@/src/features/notifications/tiers";

export const OVERDUE_OBLIGATION_DAYS = 7;
export const OVERDUE_OBLIGATION_MS = OVERDUE_OBLIGATION_DAYS * 24 * 60 * 60 * 1000;

/** Statuses of a todo the user still owes. Deferred is a snooze: the user chose later. */
export const OPEN_TODO_STATUSES = ["pending", "in_progress"] as const;

const record = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;

const text = (value: unknown) => (typeof value === "string" ? value.trim().toLowerCase() : "");

function priorityOf(task: Record<string, unknown>): number | null {
  const value = task.priority;
  if (typeof value === "number") return value;
  if (typeof value === "string" && value.trim()) return Number(value);
  return null;
}

/** An open high-priority todo, whatever its due date. */
export function isOpenMustDo(task: unknown): boolean {
  const item = record(task);
  if (!item) return false;
  const status = text(item.status);
  if (status && !(OPEN_TODO_STATUSES as readonly string[]).includes(status)) return false;
  return isHighTaskPriority(priorityOf(item));
}

/** The due time (ms epoch) of a todo, or null. */
export function todoDueTime(task: unknown): number | null {
  return notificationTime(record(task)?.dueDate);
}

/**
 * A must-do the overdue obligation owns at `now`: due, and overdue by less
 * than the cap. A date-only due is stored at an edge of its local day, so it
 * counts from that edge, as the reminder path does.
 */
export function isOverdueObligation(task: unknown, now: number): boolean {
  if (!isOpenMustDo(task)) return false;
  const due = todoDueTime(task);
  return due !== null && due <= now && now - due < OVERDUE_OBLIGATION_MS;
}

/** A must-do past the overdue cap: the stale task digest owns it from here. */
export function isReleasedOverdueMustDo(task: unknown, now: number): boolean {
  if (!isOpenMustDo(task)) return false;
  const due = todoDueTime(task);
  return due !== null && now - due >= OVERDUE_OBLIGATION_MS;
}
